/*
 *   Copyright (c) 2021 vlinder Labs Private Limited
 *   All rights reserved.
 *   This software is proprietary of Vlinder Labs Pvt. Ltd and can not be distributed / used for any purpose outside of Vlinder Labs Pvt. Ltd without explicit written consent from the copyright owner.
 */

import {
  IAgentPlugin,
  IPluginMethodMap,
} from "@vlinder-web/plugin-agent-service-react";
import { GraphqlServerModule } from "@vlinder-web/graphql-server-module-react";
import { CREATE_PAY_TX } from "../graphql/createPayTx.mutation";
import { CostumePageService } from "../../costumePageService";
import { VApplication } from "../../../application";
import axios from "axios";
import { v4 as uuidv4 } from "uuid";
import { LoggerModule } from "@vlinder-web/logger-module-react";

const log: any = LoggerModule?.getInstance();
const getGraphQLModule = () => {
  const app = VApplication.getInstance();
  return app.getSync("services.GraphqlServerModule") as GraphqlServerModule;
};

export interface CreatePayTxProps {
  amount: number;
  currency?: string;
  notes?: string;
  payOrder?: boolean;
  mode?: string;
}
export interface ICreatePayTxMethods extends IPluginMethodMap {
  createPayTx(args: any): Promise<any>;
}

export class CreatePayTxService implements IAgentPlugin {
  readonly methods: ICreatePayTxMethods;

  constructor() {
    this.methods = {
      createPayTx: this.createPayTx.bind(this),
    };
  }

  async createPayTx(args: any) {
    const graphqlModule = getGraphQLModule();
    const client = graphqlModule.getClient("graphql-gateway");
    if (args?.payOrder?.id === null || args?.payOrder?.id === undefined) {
      throw Error("Pay order cannot be null or empty");
    }

    // Held tickets (#736): one Mas Camp cash payment for the costume share (the same
    // allocations today's cash entry takes) and the tickets in full.
    if (args?.event?.ticketOrderId) {
      const res = await CostumePageService.getInstance().payTicketsAtMasCamp({
        boxId: args?.event?.boxId || args?.id,
        ticketOrderId: args.event.ticketOrderId,
        allocations: args?.event?.allocations || [],
        email: args?.event?.invoice_email || undefined,
        ...(args?.event?.discount ? { discountCode: args.event.discount } : {}),
      } as any);
      return {
        createCashPayTx: {
          amount: (res?.costumeAmount || 0) + (res?.ticketTotal || 0),
          id: res?.txId,
        },
        payTicketsAtMasCamp: res,
      };
    }
    console.log("args?.discount", args)
    let mode = parseFloat(args?.event?.amount) === 0 &&
      args?.event?.discount ? "Cash" : args?.event?.paymentType
    let variables = {};
    if (args?.event?.discount) {
      variables = {
        ...variables,
        discountCode: args?.event?.discount
      }
    }
    if (args?.event?.invoice_email) {
      variables = {
        ...variables,
        email: args?.event?.invoice_email,
      }
    } else {
      variables = {
        ...variables,
        email: null,
      }
    }
    const isRefund =
      typeof mode === "string" &&
      (mode.startsWith("Refund") || mode.toLowerCase().includes("refund"));

    let amountVal = parseFloat(args?.event?.amount);
    if (isRefund && !isNaN(amountVal) && amountVal > 0) {
      amountVal = -amountVal;
    }

    let rawAllocations = args?.event?.allocations;
    if (isRefund && Array.isArray(rawAllocations)) {
      rawAllocations = rawAllocations.map((alloc: any) => {
        const itemAmt = parseFloat(alloc?.amount);
        return {
          ...alloc,
          amount: !isNaN(itemAmt) && itemAmt > 0 ? -itemAmt : alloc?.amount,
        };
      });
    }

    variables = {
      ...variables,
      data: {
        amount: amountVal,
        currency: args?.event?.currency,
        notes: args?.event?.notes,
        mode: mode,
        payOrder: args?.payOrder?.id,
        txId: uuidv4(),
        ...(rawAllocations && {
          allocations: rawAllocations,
        }),
      },
    };
    try {
      if (client) {
        const res = await client.mutate({
          mutation: CREATE_PAY_TX,
          variables: variables,
          fetchPolicy: "no-cache",
        });
        if (res?.data) {
          return {
            ...res?.data,
          };
        }
      }
    } catch (err: any) {
      let _display_message =
        err?.graphQLErrors &&
        Array.isArray(err?.graphQLErrors) &&
        err?.graphQLErrors.find((item: any) => item.extensions);
      if (_display_message?.extensions?.display_message) {
        throw Error(_display_message?.extensions?.display_message);
      }

      throw err;
    }
  }
}
